import axios from "axios";
import { baseurl } from "../Basurl/Baseurl";

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
});

const pickFirst = (...values) => {
  for (const value of values) {
    if (value !== undefined && value !== null && value !== "") return value;
  }
  return "";
};

const toPageMeta = ({ total, limit, offset, page, hasNextPage }) => {
  const safeLimit = Math.max(1, Number(limit) || 10);
  const safeTotal = Math.max(0, Number(total) || 0);
  const currentPage =
    Number(page) ||
    Math.floor(Math.max(0, Number(offset) || 0) / safeLimit) + 1;
  const lastPageFromTotal =
    safeTotal > 0 ? Math.max(1, Math.ceil(safeTotal / safeLimit)) : null;

  let lastPage = lastPageFromTotal;
  if (lastPage == null) {
    if (hasNextPage === true) lastPage = currentPage + 1;
    else lastPage = Math.max(1, currentPage);
  }

  return {
    currentPage,
    lastPage: Math.max(1, lastPage),
    total: safeTotal,
  };
};

const unwrapListPayload = (response, requestedPage = 1, requestedLimit = 10) => {
  const root = response?.data ?? {};
  const nested = root?.data;

  if (Array.isArray(nested)) {
    const meta = toPageMeta({
      total: root.total || nested.length,
      limit: requestedLimit,
      page: root.current_page || root.page || requestedPage,
    });
    return { items: nested, ...meta };
  }

  if (nested && typeof nested === "object") {
    const items =
      nested.customers ||
      nested.data ||
      nested.contacts ||
      nested.leads ||
      nested.rows ||
      nested.list ||
      [];
    const limit = nested.limit || requestedLimit;
    const meta = toPageMeta({
      total:
        nested.total_customers ||
        nested.total ||
        root.total ||
        (Array.isArray(items) ? items.length : 0),
      limit,
      offset: nested.offset,
      page:
        nested.current_page ||
        nested.page ||
        root.current_page ||
        root.page ||
        requestedPage,
      hasNextPage: nested.has_next_page,
    });
    return {
      items: Array.isArray(items) ? items : [],
      ...meta,
    };
  }

  if (Array.isArray(root)) {
    return {
      items: root,
      currentPage: 1,
      lastPage: 1,
      total: root.length,
    };
  }

  return { items: [], currentPage: 1, lastPage: 1, total: 0 };
};

/** Normalize campaign contact / chatbot customer payload */
export const normalizeLeadItem = (item = {}, type = "campaign") => {
  const traits =
    item.traits && typeof item.traits === "object" ? item.traits : {};

  const countryCode = pickFirst(item.country_code, item.countryCode);
  const phoneNumber = pickFirst(
    item.phone_number,
    item.phone,
    item.mobile,
    item.mobile_number,
    item.whatsapp,
    item.contact_number,
    item.channel_account_identifier,
  );
  const phone = countryCode
    ? `${countryCode} ${phoneNumber}`.trim()
    : phoneNumber;

  const source = pickFirst(
    traits._internal_lead_source,
    item.channel_type,
    traits.source_url,
    item.source,
    item.campaign_name,
    item.campaign,
    item.utm_source,
    item.platform,
    item.channel,
    type === "campaign" ? "Campaign" : "Chatbot",
  );

  const alreadyExists =
    typeof item.alreadyExists === "boolean"
      ? item.alreadyExists
      : typeof item.already_exists === "boolean"
        ? item.already_exists
        : null;

  return {
    id: item.id || item._id || item.lead_id || item.contact_id,
    enquiryId: pickFirst(
      item.enquiryId,
      item.enquiry_id,
      item.lead_id,
      item.contact_id,
      item.reference_id,
      item.id,
    ),
    name: pickFirst(
      traits.name,
      item.name,
      item.full_name,
      item.contact_name,
      item.patient_name,
      [item.first_name, item.last_name].filter(Boolean).join(" "),
    ),
    email: pickFirst(
      traits.email,
      item.email,
      item.email_address,
      item.contact_email,
    ),
    phone,
    phoneNumber,
    countryCode,
    country: pickFirst(item.country, item.country_name, countryCode),
    treatingIn: pickFirst(item.treating_in, item.treatingIn),
    source,
    channelType: pickFirst(item.channel_type, traits._internal_lead_source),
    sourceUrl: pickFirst(traits.source_url, item.source_url),
    sourceId: pickFirst(traits.source_id, item.source_id),
    alreadyExists,
    status:
      alreadyExists === true
        ? "Already Exists"
        : alreadyExists === false
          ? "New"
          : pickFirst(item.status, item.lead_status, item.contact_status, "—"),
    date: pickFirst(
      item.created_at_utc,
      item.date,
      item.created_at,
      item.createdAt,
      item.updated_at,
      item.modified_at_utc,
      item.submitted_at,
    ),
    message: pickFirst(
      item.message,
      item.query,
      item.notes,
      item.remark,
      item.description,
    ),
    matchedEnquiry: item.matchedEnquiry || item.matched_enquiry || null,
    whatsappOptedIn: traits.whatsapp_opted_in,
    raw: item,
  };
};

/** GET /api/crm/campaign/contacts?page=&limit= */
export const fetchCampaignContacts = async ({ page = 1, limit = 10 } = {}) => {
  const response = await axios.get(`${baseurl}crm/campaign/contacts`, {
    params: { page, limit },
    headers: authHeaders(),
  });
  const payload = unwrapListPayload(response, page, limit);
  return {
    data: payload.items.map((item) => normalizeLeadItem(item, "campaign")),
    currentPage: payload.currentPage,
    lastPage: Math.max(1, payload.lastPage),
    total: payload.total,
  };
};

/** GET /api/crm/chatbot-leads?page=&limit= */
export const fetchChatbotLeads = async ({ page = 1, limit = 10 } = {}) => {
  const response = await axios.get(`${baseurl}crm/chatbot-leads`, {
    params: { page, limit },
    headers: authHeaders(),
  });
  const payload = unwrapListPayload(response, page, limit);
  return {
    data: payload.items.map((item) => normalizeLeadItem(item, "chatbot")),
    currentPage: payload.currentPage,
    lastPage: Math.max(1, payload.lastPage),
    total: payload.total,
  };
};
